import { useState } from "react";
import { Plus } from "lucide-react";
import { api, type MenuCategoryDto, MenuItemDto, MenuPackageDto } from "../../lib/api";
import {
  ActionMessage,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Modal,
  MoneyInput,
  PanelSection,
  RowActions,
  Select,
  useAdminAction,
} from "./shared";

interface ItemForm {
  categoryId: number;
  packageId: number | null;
  name: string;
  description: string;
  isVeg: boolean;
  dietTag: "veg" | "nonveg" | "beverage" | "water";
  vendorRef: string;
  perPersonPaise: number | null;
  published: boolean;
}

export default function MenuPanel({
  categories,
  packages,
  items,
  onSaved,
}: {
  categories: MenuCategoryDto[];
  packages: MenuPackageDto[];
  items: MenuItemDto[];
  onSaved: () => void;
}) {
  const { message, busy, run } = useAdminAction();
  const [newCat, setNewCat] = useState("");
  const [pkgTarget, setPkgTarget] = useState<MenuPackageDto | "new" | null>(null);
  const [pkgForm, setPkgForm] = useState({ name: "", description: "", perPersonPaise: 0, published: false });
  const [itemTarget, setItemTarget] = useState<MenuItemDto | "new" | null>(null);
  const [itemForm, setItemForm] = useState<ItemForm>({
    categoryId: 0,
    packageId: null,
    name: "",
    description: "",
    isVeg: true,
    dietTag: "veg",
    vendorRef: "",
    perPersonPaise: null,
    published: false,
  });

  const addCategory = () =>
    run(async () => {
      await api.adminSend("/menu/categories", "POST", { name: newCat });
      setNewCat("");
      onSaved();
    }, "Menu category added.");

  const savePackage = () =>
    run(async () => {
      const payload = {
        name: pkgForm.name,
        description: pkgForm.description,
        perPersonPaise: pkgForm.perPersonPaise ?? 0,
        published: pkgForm.published,
        sortOrder: 0,
      };
      if (pkgTarget === "new") await api.adminSend("/menu/packages", "POST", payload);
      else if (pkgTarget) await api.adminSend(`/menu/packages/${pkgTarget.id}`, "PUT", payload);
      setPkgTarget(null);
      onSaved();
    }, "Menu package saved.");

  const saveItem = () =>
    run(async () => {
      const payload = {
        categoryId: itemForm.categoryId,
        packageId: itemForm.packageId,
        name: itemForm.name,
        description: itemForm.description,
        isVeg: itemForm.dietTag !== "nonveg",
        dietTag: itemForm.dietTag,
        imageUrl: null,
        vendorRef: itemForm.vendorRef || null,
        perPersonPaise: itemForm.perPersonPaise ?? 0,
        published: itemForm.published,
      };
      if (itemTarget === "new") await api.adminSend("/menu/items", "POST", payload);
      else if (itemTarget) await api.adminSend(`/menu/items/${itemTarget.id}`, "PUT", payload);
      setItemTarget(null);
      onSaved();
    }, "Menu item saved.");

  return (
    <PanelSection
      title="Menu categories, packages & items"
      description="Draft items stay invisible to students until published. Do not publish unpriced choices without adding vendor quotes first — the site will warn you."
      action={
        <Button
          onClick={() => {
            setItemForm({
              categoryId: categories[0]?.id ?? 0,
              packageId: null,
              name: "",
              description: "",
              isVeg: true,
              dietTag: "veg",
              vendorRef: "",
              perPersonPaise: null,
              published: false,
            });
            setItemTarget("new");
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> Add menu item
        </Button>
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Field label="New menu category" hint="e.g. Vegetarian soup, Main course, Desserts.">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} maxLength={80} />
          </Field>
        </div>
        <Button onClick={addCategory} loading={busy} disabled={!newCat.trim()}>
          Add category
        </Button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {categories.map((c) => (
          <span key={c.id} className="rounded-full bg-wandor-prompt/12 px-3 py-1 text-xs font-medium text-wandor-prompt">
            {c.name}{" "}
            <button
              type="button"
              className="ml-1 text-wandor-prompt/70 hover:text-white"
              aria-label={`Delete category ${c.name}`}
              onClick={() => {
                if (window.confirm(`Delete category “${c.name}” and all its items?`)) {
                  void run(async () => {
                    await api.adminSend(`/menu/categories/${c.id}`, "DELETE");
                    onSaved();
                  }, "Category deleted.");
                }
              }}
            >
              ✕
            </button>
          </span>
        ))}
      </div>

      <h3 className="mt-8 text-lg font-semibold text-white">Menu packages</h3>
      <p className="mt-1 text-sm text-wandor-muted">Whole packages students can vote on, priced per person.</p>
      <div className="mt-3 space-y-3">
        {packages.map((p) => (
          <Card key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <p className="font-medium text-white">{p.name}</p>
              <p className="text-sm text-wandor-muted">
                ₹{(p.perPersonPaise / 100).toLocaleString("en-IN")} per person ·{" "}
                {p.published ? "published" : "draft"}
              </p>
            </div>
            <RowActions
              onEdit={() => {
                setPkgForm({
                  name: p.name,
                  description: p.description,
                  perPersonPaise: p.perPersonPaise,
                  published: p.published,
                });
                setPkgTarget(p);
              }}
              onDelete={() => {
                if (window.confirm(`Delete package “${p.name}”?`)) {
                  void run(async () => {
                    await api.adminSend(`/menu/packages/${p.id}`, "DELETE");
                    onSaved();
                  }, "Package deleted.");
                }
              }}
            />
          </Card>
        ))}
        <Button
          variant="secondary"
          onClick={() => {
            setPkgForm({ name: "", description: "", perPersonPaise: 0, published: false });
            setPkgTarget("new");
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> Add package
        </Button>
      </div>

      <h3 className="mt-8 text-lg font-semibold text-white">Menu items</h3>
      <div className="mt-3 space-y-3">
        {items.map((it) => (
          <Card key={it.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <p className="font-medium text-white">
                {it.name}{" "}
                <span className={`text-xs ${it.isVeg ? "text-emerald-300" : "text-amber-300"}`}>
                  {it.isVeg ? "· Veg" : "· Non-veg"}
                </span>
              </p>
              <p className="text-sm text-wandor-muted">
                {categories.find((c) => c.id === it.categoryId)?.name ?? "Uncategorised"} ·{" "}
                {it.perPersonPaise > 0 ? `₹${(it.perPersonPaise / 100).toLocaleString("en-IN")} per person` : "no price"} ·{" "}
                {it.published ? "published" : "draft"}
              </p>
            </div>
            <RowActions
              onEdit={() => {
                setItemForm({
                  categoryId: it.categoryId,
                  packageId: it.packageId,
                  name: it.name,
                  description: it.description,
                  isVeg: it.isVeg,
                  dietTag: it.dietTag ?? (it.isVeg ? "veg" : "nonveg"),
                  vendorRef: it.vendorRef ?? "",
                  perPersonPaise: it.perPersonPaise,
                  published: it.published,
                });
                setItemTarget(it);
              }}
              onDelete={() => {
                if (window.confirm(`Delete menu item “${it.name}”?`)) {
                  void run(async () => {
                    await api.adminSend(`/menu/items/${it.id}`, "DELETE");
                    onSaved();
                  }, "Item deleted.");
                }
              }}
            />
          </Card>
        ))}
      </div>

      <ActionMessage message={message} />

      {pkgTarget && (
        <Modal title={pkgTarget === "new" ? "Add menu package" : "Edit menu package"} onClose={() => setPkgTarget(null)}>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Package name" htmlFor="mp-name">
              <Input id="mp-name" value={pkgForm.name} onChange={(e) => setPkgForm((f) => ({ ...f, name: e.target.value }))} maxLength={120} />
            </Field>
            <MoneyInput
              label="Price per person (₹)"
              paise={pkgForm.perPersonPaise}
              onChangePaise={(p) => setPkgForm((f) => ({ ...f, perPersonPaise: p ?? 0 }))}
            />
            <div className="sm:col-span-2">
              <Field label="Description">
                <textarea
                  className="w-full min-h-[70px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={pkgForm.description}
                  onChange={(e) => setPkgForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={2000}
                />
              </Field>
            </div>
            <Checkbox
              label="Published (visible to students)"
              checked={pkgForm.published}
              onChange={(e) => setPkgForm((f) => ({ ...f, published: e.target.checked }))}
            />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setPkgTarget(null)}>Cancel</Button>
            <Button onClick={savePackage} loading={busy}>Save package</Button>
          </div>
        </Modal>
      )}

      {itemTarget && (
        <Modal title={itemTarget === "new" ? "Add menu item" : "Edit menu item"} onClose={() => setItemTarget(null)}>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Item name" htmlFor="mi-name">
              <Input id="mi-name" value={itemForm.name} onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))} maxLength={160} />
            </Field>
            <Field label="Category" htmlFor="mi-cat">
              <Select
                id="mi-cat"
                value={itemForm.categoryId}
                onChange={(e) => setItemForm((f) => ({ ...f, categoryId: Number(e.target.value) }))}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description">
                <textarea
                  className="w-full min-h-[70px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={itemForm.description}
                  onChange={(e) => setItemForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={2000}
                />
              </Field>
            </div>
            <Field label="Package (optional)" htmlFor="mi-pkg">
              <Select
                id="mi-pkg"
                value={itemForm.packageId ?? ""}
                onChange={(e) =>
                  setItemForm((f) => ({ ...f, packageId: e.target.value ? Number(e.target.value) : null }))
                }
              >
                <option value="">No package</option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </Field>
            <MoneyInput
              label="Cost per person (₹)"
              paise={itemForm.perPersonPaise}
              onChangePaise={(p) => setItemForm((f) => ({ ...f, perPersonPaise: p }))}
            />
            <Field label="Vendor / quote reference" htmlFor="mi-vendor">
              <Input id="mi-vendor" value={itemForm.vendorRef} onChange={(e) => setItemForm((f) => ({ ...f, vendorRef: e.target.value }))} maxLength={200} />
            </Field>
            <Field label="Dietary indicator" htmlFor="mi-diet">
              <Select
                id="mi-diet"
                value={itemForm.dietTag}
                onChange={(e) =>
                  setItemForm((f) => ({
                    ...f,
                    dietTag: e.target.value as ItemForm["dietTag"],
                    isVeg: e.target.value !== "nonveg",
                  }))
                }
              >
                <option value="veg">Vegetarian</option>
                <option value="nonveg">Non-vegetarian</option>
                <option value="beverage">Beverage</option>
                <option value="water">Water</option>
              </Select>
            </Field>
            <Checkbox
              label="Published (visible to students)"
              checked={itemForm.published}
              onChange={(e) => setItemForm((f) => ({ ...f, published: e.target.checked }))}
            />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setItemTarget(null)}>Cancel</Button>
            <Button onClick={saveItem} loading={busy}>Save item</Button>
          </div>
        </Modal>
      )}
    </PanelSection>
  );
}
